import React, { useEffect, useMemo, useState } from "react";
import { Calendar, Clock, ChefHat, ExternalLink, Phone } from "lucide-react";
import api from "@/lib/api";
import { useSection } from "@/context/SiteContext";
import { telHref } from "@/lib/site";

const DAY_ORDER = ["Måndag", "Tisdag", "Onsdag", "Torsdag", "Fredag", "Lördag", "Söndag"];
const DAY_SHORT = { Måndag: "Mån", Tisdag: "Tis", Onsdag: "Ons", Torsdag: "Tor", Fredag: "Fre", Lördag: "Lör", Söndag: "Sön" };

// JS getDay(): 0=Sun..6=Sat → map to Swedish week (Mon=0 ... Sun=6)
function todayIndex() {
  const js = new Date().getDay();
  return js === 0 ? 6 : js - 1;
}

export default function Lunch() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(todayIndex());
  const contact = useSection("contact");
  const tel = telHref(contact.phone_tel, contact.phone);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/lunch");
        setData(data);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const days = useMemo(() => {
    if (!data?.days) return [];
    const byName = Object.fromEntries(data.days.map((d) => [d.name, d]));
    return DAY_ORDER.map((n) => byName[n] || { name: n, date: "", dishes: [], price: null, note: null });
  }, [data]);

  const active = days[activeIdx] || { dishes: [] };
  const week = data?.week;
  const buffetTitle = data?.buffet_title || "Lunchbuffé";
  const buffetSubtitle = data?.buffet_subtitle || "";
  const infoText = data?.info_text || "";

  return (
    <section id="lunch" className="py-14 md:py-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative bg-[var(--text)] text-white rounded-3xl overflow-hidden shadow-2xl">
          {/* Decorative sun */}
          <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-gradient-to-br from-[#FFB663] via-[#E04F39] to-transparent opacity-40 blur-2xl pointer-events-none" />

          <div className="relative grid lg:grid-cols-12 gap-0">
            {/* Left intro */}
            <div className="lg:col-span-4 p-8 lg:p-12 border-b lg:border-b-0 lg:border-r border-white/10">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-[#FFB663]">
                <ChefHat size={14} /> Dagens lunch
              </div>
              <h2 className="font-serif-display text-4xl sm:text-5xl font-black tracking-tight mt-4 leading-[1.05]">
                Lunch <br />
                <span className="italic font-medium text-[#FFB663]">vecka {week ?? "—"}</span>
              </h2>
              <p className="mt-5 text-white/70 leading-relaxed">{buffetTitle} med tre rätter, sallader och nybakat bröd.</p>
              <div className="mt-6 flex flex-wrap gap-3 text-sm">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5">
                  <Clock size={14} className="text-[#FFB663]" /> Lunch mån–fre
                </span>
                {active.price && (
                  <span className="inline-flex items-center gap-2 rounded-full bg-[#E04F39] text-white px-3 py-1.5 font-semibold">
                    {active.price} kr
                  </span>
                )}
              </div>
              <a
                href={tel}
                data-testid="lunch-call-button"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#FFB663] hover:bg-[#FFA243] text-stone-900 px-5 py-3 font-bold transition-colors"
              >
                <Phone size={15} /> Boka lunchbord
              </a>
            </div>

            {/* Right content */}
            <div className="lg:col-span-8 p-8 lg:p-12">
              {/* Day tabs */}
              <div className="flex flex-wrap gap-1.5">
                {days.map((d, i) => {
                  const isActive = i === activeIdx;
                  const isToday = i === todayIndex();
                  return (
                    <button
                      key={d.name}
                      onClick={() => setActiveIdx(i)}
                      data-testid={`lunch-day-${d.name.toLowerCase()}`}
                      className={`relative inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-all ${
                        isActive
                          ? "bg-[#FFB663] text-stone-900 shadow-lg shadow-[#FFB663]/20"
                          : "bg-white/5 text-white/70 hover:bg-white/10 hover:text-white"
                      }`}
                    >
                      <span className="hidden sm:inline">{d.name}</span>
                      <span className="sm:hidden">{DAY_SHORT[d.name]}</span>
                      {d.date && <span className={`text-[11px] ${isActive ? "text-stone-700" : "text-white/40"}`}>{d.date}</span>}
                      {isToday && (
                        <span className={`absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#FFB663] ${isActive ? "ring-2 ring-stone-900" : "ring-2 ring-[var(--text)]"}`} />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Active day content */}
              <div className="mt-8 min-h-[260px]">
                {loading ? (
                  <div className="text-white/50 text-center py-12">Laddar lunchmenyn…</div>
                ) : active.dishes.length === 0 ? (
                  <div className="rounded-2xl bg-white/5 border border-white/10 p-8 text-center">
                    <Calendar size={28} className="mx-auto text-[#FFB663] mb-3" />
                    <div className="font-serif-display text-2xl font-bold">{active.note || "Ingen lunch denna dag"}</div>
                    <div className="text-white/50 text-sm mt-2">Vi öppnar igen nästa vardag kl 11.00</div>
                  </div>
                ) : (
                  <>
                    {/* Buffet header */}
                    <div className="flex items-baseline justify-between gap-4 pb-3 border-b border-white/10 mb-4">
                      <div>
                        <div className="font-serif-display text-2xl font-bold">{buffetTitle}</div>
                        {(buffetSubtitle || active.note) && (
                          <div className="text-sm text-white/60 mt-1">{active.note || buffetSubtitle}</div>
                        )}
                      </div>
                      {active.price && (
                        <div className="font-serif-display text-3xl font-black text-[#FFB663] tabular-nums shrink-0">
                          {active.price} kr
                        </div>
                      )}
                    </div>
                    <div className="space-y-3">
                      {active.dishes.map((d, i) => {
                        const dish = typeof d === "string" ? { name: d, description: "" } : d;
                        return (
                          <article
                            key={`${active.day}-${dish.name}-${i}`}
                            data-testid={`lunch-dish-${i}`}
                            className="group flex items-start gap-4 rounded-2xl bg-white/5 border border-white/10 hover:border-[#FFB663]/40 p-5 transition-colors"
                          >
                            <span className="font-serif-display text-3xl font-black text-[#FFB663]/70 leading-none w-10 shrink-0 mt-0.5">
                              {String(i + 1).padStart(2, "0")}
                            </span>
                            <div className="flex-1">
                              <div className="font-semibold text-base sm:text-lg leading-snug">{dish.name}</div>
                              {dish.description && (
                                <div className="text-sm text-white/60 mt-1 leading-relaxed">{dish.description}</div>
                              )}
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              {/* Always-on info text removed per request */}

              <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-3 text-xs text-white/40">
                <span>Källa: matochmat.se · uppdateras automatiskt</span>
                {data?.source_url && data.source_url !== "manual" && (
                  <a
                    href={data.source_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 hover:text-white"
                  >
                    Se hela veckan <ExternalLink size={11} />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
